import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

export function Card({ className, children, interactive, ...rest }: HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      className={cn(
        "rounded-card border border-line bg-surface",
        interactive && "transition-[border-color,background] hover:border-primary/70 hover:bg-[#212124]",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  actions,
  className,
  icon,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
  icon?: ReactNode;
}) {
  return (
    <div className={cn("flex items-start justify-between gap-3 px-4 pt-4", className)}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-[15px] font-semibold">
          {icon}
          {title}
        </div>
        {subtitle && <div className="mt-0.5 text-[12.5px] text-muted">{subtitle}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Section title row: "Action Needed 9 ............ View All Recommendations" */
export function SectionHeader({
  title,
  count,
  action,
  info,
  className,
}: {
  title: ReactNode;
  count?: number | string | null;
  action?: ReactNode;
  info?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mb-3 flex items-center justify-between gap-3", className)}>
      <h2 className="flex items-center gap-2 text-[17px] font-semibold tracking-[-0.01em]">
        {title}
        {count !== undefined && count !== null && <span className="text-sm font-normal text-muted tnum">{count}</span>}
        {info}
      </h2>
      {action && <div className="shrink-0 text-[13px]">{action}</div>}
    </div>
  );
}

/** Small-caps label ("UPCOMING EVENTS", "START A NEW CREATIVE"). */
export function Eyebrow({ children, className, icon }: { children: ReactNode; className?: string; icon?: ReactNode }) {
  return (
    <div className={cn("flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted", className)}>
      {icon}
      {children}
    </div>
  );
}

/** Label/value stat row. */
export function Stat({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-center justify-between gap-3 py-1.5 text-[13px]", className)}>
      <span className="text-muted">{label}</span>
      <span className="font-semibold tnum">{value}</span>
    </div>
  );
}
